import React, { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { CircleAlert, CircleCheck, FileSpreadsheet, Replace, TriangleAlert, Upload } from "lucide-react";
import { Pharmacy } from "../types";
import { adminFetch } from "../api";
import { Alert, Button, Field, ICON, Modal, Muted, Select, Spinner } from "../admin/ui";
import { MESES, capitalizar, listarDias, normalizar } from "../admin/turnos";

interface OriginalExcelUploaderProps {
  onClose: () => void;
  onUploadSuccess: (message: string) => void;
  /** Mes (1-12) y año que está mirando la pantalla de Turnos: el modal arranca ahí. */
  month?: number;
  year?: number;
}

type ParsedDay = { day: string; dayNum: number; pharmacies: string[] };
type Linea = { tipo: "ok" | "aviso" | "bloqueo"; texto: React.ReactNode };

/** Valor del Select para «Excluir este nombre» (un ID del catálogo nunca empieza con «_»). */
const EXCLUIR = "__excluir__";

/** El mismo slug con el que el servidor armaba los IDs del catálogo (P-3). */
const slug = (s: string) =>
  normalizar(s)
    .replace(/[^a-z0-9]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");

// Palabras que no sirven para parecerse: «Farmacia San Martín» no se parece a «Farmacia San José»
const PALABRAS_VACIAS = new Set(["farmacia", "farmacias", "farm", "del", "las", "los", "san", "santa", "santo"]);
const palabras = (s: string) =>
  normalizar(s)
    .split(/[^a-z0-9]+/)
    .filter((w) => w.length >= 3 && !PALABRAS_VACIAS.has(w));
const clave = (s: string) => palabras(s).join("");

/**
 * La farmacia del catálogo más parecida a un nombre que no coincide exacto: primero por
 * inclusión («Farmacia Pérez SRL» contiene «Pérez»), después por palabras en común.
 * Si no hay ninguna, undefined: el Select queda en «Elegí qué hacer…».
 */
function masParecida(nombre: string, catalogo: Pharmacy[]): Pharmacy | undefined {
  const k = clave(nombre);
  const ws = palabras(nombre);
  let mejor: Pharmacy | undefined;
  let puntaje = 0;
  for (const p of catalogo) {
    const c = clave(p.nombre);
    let s = 0;
    if (k.length > 3 && c.length > 3 && (k.includes(c) || c.includes(k))) {
      s = 1000 - Math.abs(k.length - c.length);
    } else {
      const pw = new Set(palabras(p.nombre));
      s = ws.filter((w) => pw.has(w)).length * 10;
    }
    if (s > puntaje) {
      puntaje = s;
      mejor = p;
    }
  }
  return mejor;
}

export const OriginalExcelUploader: React.FC<OriginalExcelUploaderProps> = ({
  onClose,
  onUploadSuccess,
  month,
  year,
}) => {
  const now = new Date();
  const [file, setFile] = useState<File | null>(null);
  const [parsing, setParsing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [parsed, setParsed] = useState<ParsedDay[] | null>(null);
  const [catalog, setCatalog] = useState<Pharmacy[]>([]);
  const [catalogLoaded, setCatalogLoaded] = useState(false);
  const [catalogFailed, setCatalogFailed] = useState(false);
  /** P-3: lo que se eligió para cada nombre que no coincide (ID del catálogo, EXCLUIR o ""). */
  const [elegidos, setElegidos] = useState<Record<string, string>>({});
  const [askHeaderOverride, setAskHeaderOverride] = useState(false);
  const [currentHeader, setCurrentHeader] = useState("");
  const [dragging, setDragging] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<number>(month ?? now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState<number>(year ?? now.getFullYear());
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetch("/api/pharmacies")
      .then((r) => {
        if (!r.ok) throw new Error(String(r.status));
        return r.json();
      })
      .then((data) => {
        if (Array.isArray(data)) {
          setCatalog(data);
        } else if (data && data.pharmacies) {
          setCatalog(data.pharmacies);
        } else {
          throw new Error("La respuesta no trae farmacias");
        }
        setCatalogLoaded(true);
      })
      .catch(() => setCatalogFailed(true));
  }, []);

  const processFile = async (target: File, override: boolean) => {
    setParsing(true);
    setError(null);
    setParsed(null);
    setElegidos({});
    setAskHeaderOverride(false);

    try {
      const data = await target.arrayBuffer();
      const workbook = XLSX.read(data);
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(firstSheet, { header: 1 }) as any[][];

      // Find the header row
      let headerRowIndex = -1;
      let firstDataRowIndex = -1;
      for (let i = 0; i < rows.length; i++) {
        if (rows[i] && rows[i].length > 0) {
          if (firstDataRowIndex === -1) firstDataRowIndex = i;
          const firstCell = String(rows[i][0] || "").toLowerCase().trim();
          if (firstCell === "dia" || firstCell === "día" || firstCell === "day") {
            headerRowIndex = i;
            break;
          }
        }
      }

      // P-8: sin la fila «Día», se avisa antes de tirar la primera fila
      if (headerRowIndex === -1 && !override) {
        const actualHeaderRow = firstDataRowIndex !== -1 ? rows[firstDataRowIndex] : [];
        const formattedActual = Array.from({ length: 5 })
          .map((_, i) => (actualHeaderRow[i] ? String(actualHeaderRow[i]) : "(vacía)"))
          .join(", ");
        setCurrentHeader(formattedActual);
        setAskHeaderOverride(true);
        return;
      }

      if (headerRowIndex === -1 && override) {
        headerRowIndex = firstDataRowIndex; // Treat the first actual row as the bad header and skip it
      }

      // Do NOT filter out rows because that ruins the groups of 5.
      // Pandas iloc[i:i+5] takes strictly 5 consecutive rows from the data, even if some cells are blank.
      const rawDataRows = rows.slice(headerRowIndex + 1);
      const results: ParsedDay[] = [];

      // Process in groups of 5 as specified
      for (let i = 0; i < rawDataRows.length; i += 5) {
        const group = rawDataRows.slice(i, i + 5);
        if (group.length === 0) continue;

        // Ensure we actually have data in the group, skip trailing empty groups
        const hasAnyData = group.some((r) => r && r.length > 0);
        if (!hasAnyData) continue;

        // Pharmacies are on the first row of the group, columns 1 to 4 (B to E)
        const pharmacies: string[] = [];
        for (let j = 1; j <= 4; j++) {
          const p = group[0]?.[j];
          if (p) {
            pharmacies.push(String(p).trim());
          }
        }

        for (const row of group) {
          if (!row || !row[0]) continue; // Skip if no day cell

          const dayStr = String(row[0]).trim();
          if (!dayStr) continue;

          // Extract day number using regex
          const match = dayStr.match(/(\d{1,2})$/);
          const dayNum = match ? parseInt(match[1], 10) : 0;

          if (dayNum > 0) {
            results.push({ day: dayStr, dayNum, pharmacies: [...pharmacies] });
          }
        }
      }

      results.sort((a, b) => a.dayNum - b.dayNum);
      setParsed(results);
    } catch (_) {
      setError("No pudimos leer el archivo. Fijate que sea el Excel del Colegio (.xlsx o .xls).");
    } finally {
      setParsing(false);
    }
  };

  const chooseFile = (f: File | undefined) => {
    if (!f) return;
    setFile(f);
    processFile(f, false);
  };

  // P-3: cada nombre distinto del archivo se resuelve contra el catálogo. Coincide exacto si su
  // slug es el ID de una farmacia o si el nombre normalizado es el de una farmacia.
  const resolucion = useMemo(() => {
    if (!parsed || !catalogLoaded) return null;
    const nombres: string[] = Array.from(new Set<string>(parsed.flatMap((r) => r.pharmacies)));
    const exactos = new Map<string, string>();
    const sinCoincidencia: string[] = [];
    const sugeridos: Record<string, Pharmacy> = {};
    for (const nombre of nombres) {
      const exacto = catalog.find((p) => p.id === slug(nombre) || normalizar(p.nombre) === normalizar(nombre));
      if (exacto) {
        exactos.set(nombre, exacto.id);
      } else {
        sinCoincidencia.push(nombre);
        const parecida = masParecida(nombre, catalog);
        if (parecida) sugeridos[nombre] = parecida;
      }
    }
    return { nombres, exactos, sinCoincidencia, sugeridos };
  }, [parsed, catalog, catalogLoaded]);

  const catalogoOrdenado = useMemo(
    () => [...catalog].sort((a, b) => a.nombre.localeCompare(b.nombre, "es")),
    [catalog],
  );

  /** Respuesta para un nombre sin coincidencia: solo lo que eligió la persona ("" = sin responder). La sugerencia no se preelige. */
  const respuesta = (nombre: string) => elegidos[nombre] ?? "";
  /** ID del catálogo al que va un nombre del archivo; null si se excluye o si falta responder. */
  const destino = (nombre: string): string | null => {
    const exacto = resolucion?.exactos.get(nombre);
    if (exacto) return exacto;
    const r = respuesta(nombre);
    return r && r !== EXCLUIR ? r : null;
  };
  const pendientes = resolucion ? resolucion.sinCoincidencia.filter((n) => respuesta(n) === "") : [];

  // P-4: se valida contra el mes y el año elegidos, cada vez que cambian
  const check = useMemo(() => {
    if (!parsed) return null;
    const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
    const inMonth = parsed.filter((r) => r.dayNum >= 1 && r.dayNum <= daysInMonth);
    const outOfMonth: number[] = Array.from(new Set<number>(parsed.filter((r) => r.dayNum > daysInMonth).map((r) => r.dayNum)));
    // Los nombres excluidos no se mandan: ese día queda sin esa farmacia
    const quedan = (r: ParsedDay) => r.pharmacies.filter((p) => elegidos[p] !== EXCLUIR);
    const withPharmacy = new Set(inMonth.filter((r) => quedan(r).length > 0).map((r) => r.dayNum));
    const missingDays: number[] = [];
    for (let d = 1; d <= daysInMonth; d++) if (!withPharmacy.has(d)) missingDays.push(d);
    const assignments = inMonth.reduce((n, r) => n + quedan(r).length, 0);

    return { daysInMonth, loadedDays: withPharmacy.size, missingDays, outOfMonth, assignments };
  }, [parsed, elegidos, selectedMonth, selectedYear]);

  const mes = MESES[selectedMonth - 1];
  // P-3: «Reemplazar» espera el catálogo y una respuesta (emparejar o excluir) por cada nombre que no coincide
  const canReplace =
    !!check &&
    !!resolucion &&
    pendientes.length === 0 &&
    check.assignments > 0 &&
    check.outOfMonth.length === 0 &&
    !parsing &&
    !uploading;

  const lineas: Linea[] = [];
  if (check) {
    if (check.outOfMonth.length > 0) {
      lineas.push({
        tipo: "bloqueo",
        texto: `El archivo trae días que ${mes} no tiene: ${check.outOfMonth.join(", ")}. Revisá el mes elegido.`,
      });
    }
    if (check.assignments === 0) {
      lineas.push({ tipo: "bloqueo", texto: "El archivo no trae ningún día con farmacias." });
    } else if (check.missingDays.length === 0) {
      lineas.push({ tipo: "ok", texto: `Están los ${check.daysInMonth} días del mes` });
    } else {
      const n = check.missingDays.length;
      const cuales = n <= 4 ? `: ${listarDias(check.missingDays)}` : "";
      lineas.push({
        tipo: "aviso",
        texto: n === 1 ? `Falta 1 día${cuales}. Va a quedar en ámbar.` : `Faltan ${n} días${cuales}. Van a quedar en ámbar.`,
      });
    }
    if (catalogFailed) {
      lineas.push({
        tipo: "bloqueo",
        texto: "No pudimos traer el catálogo de farmacias. Cerrá y volvé a abrir para reintentar.",
      });
    } else if (!resolucion) {
      lineas.push({ tipo: "aviso", texto: "Trayendo el catálogo de farmacias…" });
    } else if (resolucion.nombres.length > 0) {
      const total = resolucion.nombres.length;
      const coinciden = resolucion.exactos.size;
      lineas.push({
        tipo: coinciden === total ? "ok" : "aviso",
        texto:
          coinciden === total
            ? `Las ${total} farmacias coinciden con el catálogo`
            : `${coinciden} de ${total} farmacias coinciden con el catálogo`,
      });
    }
  }

  const uploadParsedData = async () => {
    if (!parsed || !check || !canReplace) return;
    setUploading(true);
    setError(null);

    try {
      // CSV con el número de día (el servidor lo valida contra el mes, P-4) y el ID del
      // catálogo (P-3). Los nombres excluidos no se mandan.
      let csvContent = "dia-inicio,farmacia_id\n";
      parsed
        .filter((row) => row.dayNum <= check.daysInMonth)
        .forEach((row) => {
          const ids = new Set<string>();
          row.pharmacies.forEach((p) => {
            const id = destino(p);
            if (id) ids.add(id);
          });
          ids.forEach((id) => {
            csvContent += `"${row.dayNum}","${id.replace(/"/g, '""')}"\n`;
          });
        });

      // Add BOM
      const blob = new Blob(["﻿" + csvContent], { type: "text/csv;charset=utf-8;" });
      const formData = new FormData();
      formData.append("file", blob, "upload.csv");
      formData.append("month", String(selectedMonth).padStart(2, "0"));
      formData.append("year", String(selectedYear));

      const res = await adminFetch("/api/upload", {
        method: "POST",
        body: formData,
      });

      const resData = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(resData.error || "El servidor no aceptó el archivo.");
      }
      onUploadSuccess(resData.message || `${capitalizar(mes)} reemplazado`);
      onClose();
    } catch (err: any) {
      setError(
        err instanceof TypeError
          ? "No hay conexión con el servidor. Revisá internet y reintentá."
          : err?.message || "No se pudo subir el archivo.",
      );
      setUploading(false);
    }
  };

  const years = Array.from(new Set([now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1, now.getFullYear() + 2, selectedYear])).sort();

  const meta = parsing ? (
    <>
      <Spinner size={12} />
      Leyendo el archivo…
    </>
  ) : check ? (
    `${check.loadedDays} días · ${check.assignments} asignaciones`
  ) : askHeaderOverride ? (
    "Falta la fila de títulos"
  ) : error ? (
    "No se pudo leer"
  ) : null;

  return (
    <Modal
      open
      onClose={() => {
        if (!uploading) onClose();
      }}
      eyebrow="Carga del mes"
      title="Subir Excel del Colegio"
      footer={
        <>
          <Button onClick={onClose} disabled={uploading}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={uploadParsedData} disabled={!canReplace && !uploading} loading={uploading}>
            {uploading ? "Reemplazando…" : `Reemplazar ${mes}`}
          </Button>
        </>
      }
    >
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,.xls"
        className="hidden"
        onChange={(e) => {
          chooseFile(e.target.files?.[0]);
          e.target.value = "";
        }}
      />

      {file ? (
        <div className="flex items-center gap-3 rounded-xl border border-adm-border bg-adm-card p-3">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-[10px] border border-adm-border bg-adm-surface text-adm-t55">
            <FileSpreadsheet {...ICON} />
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="truncate text-[14px] font-medium text-adm-text">{file.name}</span>
            {meta && <span className="flex flex-wrap items-center gap-1.5 text-[12px] text-adm-t55">{meta}</span>}
          </div>
          <Button size="sm" onClick={() => inputRef.current?.click()} disabled={uploading || parsing}>
            Cambiar
          </Button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            chooseFile(e.dataTransfer.files?.[0]);
          }}
          className={`flex w-full cursor-pointer flex-col items-center gap-1 rounded-xl border border-dashed bg-adm-surface p-4 text-center text-[13px] transition-colors ${
            dragging
              ? "border-adm-acento text-adm-text"
              : "border-adm-border-2 text-adm-t55 hover:border-adm-acento hover:text-adm-text"
          }`}
        >
          <Upload {...ICON} />
          <b className="font-medium text-adm-text">Subí el Excel del mes</b>
          <span>.xlsx o .xls, el que manda el Colegio</span>
        </button>
      )}

      <div className="grid grid-cols-2 gap-4 max-md:grid-cols-1">
        <Field label="Mes" htmlFor="upl-mes">
          <Select
            id="upl-mes"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(Number(e.target.value))}
            disabled={uploading}
          >
            {MESES.map((m, i) => (
              <option key={m} value={i + 1}>
                {capitalizar(m)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Año" htmlFor="upl-anio">
          <Select
            id="upl-anio"
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            disabled={uploading}
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      {askHeaderOverride && file && (
        <div className="flex flex-col gap-3">
          <Alert tone="amber">
            La primera fila no dice «Día».{" "}
            <Muted>
              Esperábamos: Día, Farmacia 1, Farmacia 2, Farmacia 3, Farmacia 4. Dice: {currentHeader}. Si seguís, esa
              fila se toma como títulos y no se carga.
            </Muted>
          </Alert>
          <div className="flex gap-2.5">
            <Button size="sm" className="flex-1" onClick={() => inputRef.current?.click()}>
              Elegir otro archivo
            </Button>
            <Button size="sm" variant="primary" className="flex-1" onClick={() => processFile(file, true)}>
              Seguir igual
            </Button>
          </div>
        </div>
      )}

      {lineas.length > 0 && (
        <div className="flex flex-col gap-2">
          {lineas.map((l, i) => (
            <div key={i} className="flex items-center gap-2.5 text-[14px] text-adm-text">
              {l.tipo === "ok" && <CircleCheck {...ICON} className="shrink-0 text-adm-acento" />}
              {l.tipo === "aviso" && <TriangleAlert {...ICON} className="shrink-0 text-adm-ambar" />}
              {l.tipo === "bloqueo" && <CircleAlert {...ICON} className="shrink-0 text-adm-rojo" />}
              <span>{l.texto}</span>
            </div>
          ))}
        </div>
      )}

      {resolucion && resolucion.sinCoincidencia.length > 0 && (
        <div className="flex flex-col gap-3">
          {resolucion.sinCoincidencia.map((nombre, i) => {
            const sugerida = resolucion.sugeridos[nombre];
            return (
              <React.Fragment key={nombre}>
                <Field
                  label={`«${nombre}» no está en el catálogo`}
                  htmlFor={`upl-nombre-${i}`}
                  hint={sugerida ? `¿Es «${sugerida.nombre}»? Elegila, o excluí este nombre.` : undefined}
                >
                  <Select
                    id={`upl-nombre-${i}`}
                    value={respuesta(nombre)}
                    onChange={(e) => setElegidos((prev) => ({ ...prev, [nombre]: e.target.value }))}
                    disabled={uploading}
                  >
                    <option value="">Elegí qué hacer…</option>
                    {sugerida && <option value={sugerida.id}>Sugerida: {sugerida.nombre}</option>}
                    {catalogoOrdenado
                      .filter((p) => p.id !== sugerida?.id)
                      .map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.nombre}
                        </option>
                      ))}
                    <option value={EXCLUIR}>Excluir este nombre</option>
                  </Select>
                </Field>
              </React.Fragment>
            );
          })}
        </div>
      )}

      {canReplace && (
        <Alert tone="amber" icon={Replace}>
          Reemplaza todo {mes}. <Muted>Los turnos que ya estaban cargados se borran y quedan los del archivo.</Muted>
        </Alert>
      )}

      {error && (
        <Alert tone="red">{error}</Alert>
      )}
    </Modal>
  );
};
